import { GameState, LobbyState } from "./types";

/**
 * What this player has to do right now, in a few words, or undefined when it is nobody's business but others'.
 * It works from the latest server state, so it stays right even while the screen is still playing animations.
 */
export function pendingAction(game: GameState, user: string): string | undefined {
  const me = game.players?.[user];
  if (!me || !me.alive) {
    return undefined;
  }
  const isPresident = game.president === user;
  switch (game.state) {
    case LobbyState.CHANCELLOR_NOMINATION:
      return isPresident ? "Nominate a Chancellor" : undefined;
    case LobbyState.CHANCELLOR_VOTING:
      return game.userVotes && Object.keys(game.userVotes).includes(user) ? undefined : "Vote on the government";
    case LobbyState.LEGISLATIVE_PRESIDENT:
      return isPresident ? "Discard a policy" : undefined;
    case LobbyState.LEGISLATIVE_CHANCELLOR:
      return game.chancellor === user ? "Enact a policy" : undefined;
    case LobbyState.LEGISLATIVE_PRESIDENT_VETO:
      return isPresident ? "Answer the veto" : undefined;
    case LobbyState.PP_PEEK:
    case LobbyState.PP_INVESTIGATE:
    case LobbyState.PP_EXECUTION:
    case LobbyState.PP_ELECTION:
      return isPresident ? "Use your power" : undefined;
    case LobbyState.POST_LEGISLATIVE:
      return isPresident ? "End your term" : undefined;
    default:
      return undefined;
  }
}

const FLASH_MS = 1000;
const REMINDER_MS = 25000;

/**
 * Draws attention to the player who has to act while the game is in a tab or window they are not looking at:
 * the tab title flashes ("🔔 Your turn: Vote on the government") and a reminder chime repeats until they come
 * back or act. Nothing happens while the game is in front of them, where the prompt itself is the alert.
 */
export class TurnAlert {
  private label: string | undefined;
  private baseTitle = "";
  private flashTimer: ReturnType<typeof setInterval> | undefined;
  private lastChime = 0;
  private showingAlert = false;

  constructor(private chime: () => void) {}

  private away(): boolean {
    return document.hidden || !document.hasFocus();
  }

  /** Tells the alert what the player has to do now (undefined when nothing). Call it after every state change. */
  update(label: string | undefined): void {
    const wasPending = this.label !== undefined;
    this.label = label;
    if (label === undefined) {
      this.stop();
    } else if (!wasPending) {
      this.lastChime = Date.now();
      this.start();
    }
  }

  private start(): void {
    this.stop(false);
    this.baseTitle = document.title.replace(/^🔔 [^|]*\| /, "");
    this.flashTimer = setInterval(() => this.tick(), FLASH_MS);
    this.tick();
  }

  private tick(): void {
    if (this.label === undefined) {
      return;
    }
    if (!this.away()) {
      this.restoreTitle();
      return;
    }
    this.showingAlert = !this.showingAlert;
    document.title = this.showingAlert ? "🔔 Your turn: " + this.label + " | " + this.baseTitle : this.baseTitle;
    // The ding that comes with the prompt may have been missed in a background tab: repeat it now and then.
    if (Date.now() - this.lastChime >= REMINDER_MS) {
      this.lastChime = Date.now();
      this.chime();
    }
  }

  private restoreTitle(): void {
    this.showingAlert = false;
    if (this.baseTitle) {
      document.title = this.baseTitle;
    }
  }

  stop(restore: boolean = true): void {
    if (this.flashTimer !== undefined) {
      clearInterval(this.flashTimer);
      this.flashTimer = undefined;
    }
    if (restore) {
      this.restoreTitle();
    }
  }
}
